import { m } from '@/paraglide/messages';

// The guest composition a booking is priced against: how many adults, the
// AGE of every child (never just a count — tourist tax and pricing rules
// are age-banded), and how many infants (under 2, don't consume capacity
// or get priced). `null` in `childrenAges` means "a child was added but we
// haven't asked their age yet" — see isPartyComplete. Never collapse that
// to 0 or any other default: an unanswered question must stay unanswered.
export interface PartyValue {
  adults: number;
  childrenAges: (number | null)[];
  infants: number;
}

// Shape of the query-string representation. Mirrors the server's own params
// (`GET /search?adults=&child_age=&child_age=&infants=`) — see docs/27 and
// the task-12 brief. `guests` is the deprecated scalar the server still
// reads as a synonym for `adults`; partyToSearch never writes it, only
// partyFromSearch reads it, as a fallback for links shared before ages
// existed.
export interface PartySearchParams {
  adults?: number;
  child_age?: number[];
  infants?: number;
  guests?: number;
}

// TanStack Router's `validateSearch` (and a raw `URLSearchParams`) can hand
// back strings, or a lone value instead of a one-item array for a
// repeated key — so partyFromSearch accepts a looser shape than what
// partyToSearch produces, and coerces defensively.
interface PartySearchInput {
  adults?: number | string;
  child_age?: number | string | (number | string)[];
  infants?: number | string;
  guests?: number | string;
}

const MIN_ADULTS = 1;

// The age band a "child" occupies, mirroring the server's own guestparty
// package: below MIN is an infant, at MAX+1 the guest is an adult. Exported
// so the picker's age options and the URL normalization below cannot drift
// apart — and because these exact numbers are a contract with the tourist-tax
// brackets that read the ages.
export const MIN_CHILD_AGE = 2;
export const MAX_CHILD_AGE = 17;

function toFiniteNumber(input: number | string | undefined): number | undefined {
  if (typeof input === 'number') return Number.isFinite(input) ? input : undefined;
  if (typeof input === 'string' && input.trim() !== '') {
    const n = Number(input);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

// normalizedPartyFromSearch is what a ROUTE should use: partyFromSearch is a
// parser and answers "what does this URL say", while a route additionally has
// to answer "what may this URL be allowed to say". A stranger can type
// anything — adults=0, child_age=99, infants=-1 — and the difference matters:
// an out-of-range child age reaches the server, which refuses it, and the
// search page renders that refusal as "those dates don't work", blaming the
// dates for a guests problem.
//
// Lives here rather than in one route so both routes that accept a party
// clean it the same way. Two copies of this rule would drift, and the one
// that drifted would be the one nobody looked at.
export function normalizedPartyFromSearch(input: PartySearchInput): PartyValue {
  const raw = partyFromSearch(input);
  return {
    adults: Math.max(MIN_ADULTS, raw.adults),
    // partyFromSearch never yields a null age (only the picker's own
    // addChild does), but PartyValue admits one, so narrow explicitly
    // instead of assuming.
    childrenAges: raw.childrenAges.filter(
      (age): age is number => age !== null && age >= MIN_CHILD_AGE && age <= MAX_CHILD_AGE,
    ),
    infants: Math.max(0, raw.infants),
  };
}

// Pure, no React — reused by both the picker (Task 12) and the routes that
// carry party state through the URL (next task).
export function partyToSearch(value: PartyValue): PartySearchParams {
  const params: PartySearchParams = { adults: value.adults };
  // A child with no age yet cannot be represented as a `child_age` entry
  // (the server param is a list of ages, not a count) — it is simply
  // omitted until answered. Callers gate navigation on isPartyComplete, but
  // this stays defensive rather than throwing so a half-filled picker never
  // corrupts the rest of the URL.
  const ages = value.childrenAges.filter((age): age is number => age !== null);
  if (ages.length > 0) params.child_age = ages;
  if (value.infants > 0) params.infants = value.infants;
  return params;
}

export function partyFromSearch(params: PartySearchInput): PartyValue {
  const adults = toFiniteNumber(params.adults) ?? toFiniteNumber(params.guests) ?? MIN_ADULTS;
  const rawAges = params.child_age;
  const ages = rawAges === undefined ? [] : Array.isArray(rawAges) ? rawAges : [rawAges];
  const childrenAges = ages
    .map((age) => toFiniteNumber(age))
    .filter((age): age is number => age !== undefined);
  const infants = toFiniteNumber(params.infants) ?? 0;
  return { adults, childrenAges, infants };
}

// The whole reason ages are collected in the picker rather than later: a
// child whose age we never asked for cannot be priced for tourist tax. A
// party is only "complete" once every child slot has an answer.
export function isPartyComplete(value: PartyValue): boolean {
  return value.adults >= MIN_ADULTS && value.childrenAges.every((age) => age !== null);
}

export function partyLabel(value: PartyValue): string {
  const parts: string[] = [
    value.adults === 1 ? m.party_label_one_adult() : m.party_label_adults({ count: value.adults }),
  ];

  const childCount = value.childrenAges.length;
  if (childCount > 0) {
    parts.push(
      childCount === 1 ? m.party_label_one_child() : m.party_label_children({ count: childCount }),
    );
  }

  if (value.infants > 0) {
    parts.push(
      value.infants === 1 ? m.party_label_one_infant() : m.party_label_infants({ count: value.infants }),
    );
  }

  return parts.join(' · ');
}
